import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import FullLogo from '../../assets/logos/full-logo.png'
import VoiceInstructionsImage from '../../assets/images/carousel-1.png'
import ConnectionInstructionsImage from '../../assets/images/carousel-2.png'
import VideoInstructionsImage from '../../assets/images/carousel-3.png'
import {
  BackButton,
  ButtonContainer,
  CarouselButton,
  CarouselContainer,
  CarouselText,
  CarouselTitle,
  ContentContainer,
  CountDownBoxWrapper,
  CountdownCard,
  CountdownCardBody,
  CountdownCardHeader,
  FooterContainer,
  ImageContainer,
  InstructionsContainer,
  InstructionsText,
  MainContainer,
  StepsContainer,
  StepsText,
} from '../styled'
import TestVideo from './testVideo'
import PrimaryButton from '../buttons/PrimaryButton'
import theme from '../../constants/theme'
import useDeviceType from '../../hooks/useDeviceType'

function CountdownItem({ label, value, isMobile }) {
  return (
    <CountdownCard isMobile={isMobile}>
      <CountdownCardHeader isMobile={isMobile}>{label}</CountdownCardHeader>
      <CountdownCardBody isMobile={isMobile}>{value}</CountdownCardBody>
    </CountdownCard>
  )
}

CountdownItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  isMobile: PropTypes.bool,
}

CountdownItem.defaultProps = {
  isMobile: false,
}

function Timer({ interviewTime, setShowCounter }) {
  const { t } = useTranslation()
  const [days, setDays] = useState(0)
  const [hours, setHours] = useState(0)
  const [minutes, setMinutes] = useState(0)
  const [seconds, setSeconds] = useState(0)
  const deviceType = useDeviceType()
  const isMobile = deviceType !== 'desktop'
  useEffect(() => {
    const countdownInterval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1)
      }
      if (seconds <= 0) {
        if (minutes <= 0) {
          if (hours <= 0) {
            if (days <= 0) {
              clearInterval(countdownInterval)
              setShowCounter(false)
            } else {
              setDays(days - 1)
              setHours(23)
              setMinutes(59)
              setSeconds(59)
            }
          } else {
            setHours(hours - 1)
            setMinutes(59)
            setSeconds(59)
          }
        } else {
          setMinutes(minutes - 1)
          setSeconds(59)
        }
      }
    }, 1000)
    return () => {
      clearInterval(countdownInterval)
    }
  })

  useEffect(() => {
    const time = new Date(`${interviewTime?.toString()}`)
    const now = new Date()
    const remainingTime = time - now
    if (remainingTime < 0) {
      setShowCounter(false)
    }
    setDays(Math.floor(remainingTime / (1000 * 60 * 60 * 24)))
    setHours(
      Math.floor((remainingTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    )
    setMinutes(Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60)))
    setSeconds(Math.floor((remainingTime % (1000 * 60)) / 1000))
  }, [interviewTime, setShowCounter])

  return (
    <CountDownBoxWrapper isMobile={isMobile}>
      <h2
        style={{
          color: '#3D3F46',
          fontSize: isMobile ? '18px' : '22px',
          fontWeight: '600',
          marginBottom: isMobile ? '16px' : '24px',
          textAlign: 'center',
        }}
      >
        {t('preInterview.yourInterviewStarts')}{' '}
      </h2>
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ gap: isMobile ? '1px' : '2px' }}
      >
        <CountdownItem
          label={t('preInterview.days')}
          value={days}
          isMobile={isMobile}
        />
        <CountdownItem
          label={t('preInterview.hours')}
          value={hours}
          isMobile={isMobile}
        />
        <CountdownItem
          label={t('preInterview.minutes')}
          value={minutes}
          isMobile={isMobile}
        />
        <CountdownItem
          label={t('preInterview.seconds')}
          value={seconds}
          isMobile={isMobile}
        />
      </div>
    </CountDownBoxWrapper>
  )
}

Timer.propTypes = {
  interviewTime: PropTypes.string,
  setShowCounter: PropTypes.func.isRequired,
}

Timer.defaultProps = {
  interviewTime: '',
}

function VideoInterviewInstructions({ setReadInstructions }) {
  const { t } = useTranslation()
  const [carouselSelected, setCarouselSelected] = useState(1)
  const deviceType = useDeviceType()
  const isMobile = deviceType !== 'desktop'
  const carouselItems = [
    {
      image: VoiceInstructionsImage,
      index: t('preInterview.first'),
      title: t('preInterview.voiceInstructions'),
      content: t('preInterview.sureMicWorks'),
    },
    {
      image: ConnectionInstructionsImage,
      index: t('preInterview.second'),
      title: t('preInterview.connectionInstructions'),
      content: t('preInterview.sureInternetStable'),
    },
    {
      image: VideoInstructionsImage,
      index: t('preInterview.third'),
      title: t('preInterview.videoInstructions'),
      content: t('preInterview.simpleBackground'),
    },
  ]
  const handleCarouselprev = () => {
    setCarouselSelected((prev) => prev - 1)
  }
  const handleCarouselNext = () => {
    if (carouselSelected < 3) {
      setCarouselSelected((prev) => prev + 1)
    } else {
      setReadInstructions(true)
    }
  }

  return (
    <>
      <InstructionsContainer>
        <InstructionsText style={{ fontSize: isMobile ? '16px' : '18px' }}>
          {t('preInterview.instructions')}
        </InstructionsText>
      </InstructionsContainer>
      <StepsContainer>
        <StepsText style={{ fontSize: isMobile ? '13px' : '14px' }}>
          {t('preInterview.beforeInterview')}{' '}
          <span style={{ fontWeight: '600' }}>
            {t('preInterview.threeEssentialSteps')}
          </span>{' '}
          {t('preInterview.toPrepare')}
        </StepsText>
      </StepsContainer>
      <ContentContainer padding={isMobile ? '15px 10px' : '30px'}>
        <CarouselContainer>
          <img
            height={isMobile ? '250px' : '303px'}
            src={carouselItems[carouselSelected - 1].image}
            alt=""
            style={{ maxWidth: '100%', objectFit: 'contain' }}
          />
          <CarouselTitle style={{ fontSize: isMobile ? '16px' : '18px' }}>
            <span style={{ color: theme.talentinoBlue }}>
              {carouselItems[carouselSelected - 1].index},
            </span>{' '}
            {carouselItems[carouselSelected - 1].title}
          </CarouselTitle>
          <CarouselText style={{ fontSize: isMobile ? '13px' : '14px' }}>
            {carouselItems[carouselSelected - 1].content}
          </CarouselText>
          <ButtonContainer>
            <CarouselButton
              selected={carouselSelected === 1}
              onClick={() => setCarouselSelected(1)}
            />
            <CarouselButton
              selected={carouselSelected === 2}
              onClick={() => setCarouselSelected(2)}
            />
            <CarouselButton
              selected={carouselSelected === 3}
              onClick={() => setCarouselSelected(3)}
            />
          </ButtonContainer>
        </CarouselContainer>
        <FooterContainer isMobile={isMobile}>
          <BackButton
            carouselSelected={carouselSelected}
            onClick={handleCarouselprev}
          >
            {t('general.back')}
          </BackButton>
          <PrimaryButton
            backgroundcolor={theme.talentinoBlue}
            hovercolor={theme.talentinoDarkerBlue}
            width={isMobile ? '120px' : '150px'}
            style={{
              whiteSpace: 'nowrap',
            }}
            onClick={handleCarouselNext}
          >
            {t('pages.nextQuestion')}
          </PrimaryButton>
        </FooterContainer>
      </ContentContainer>
    </>
  )
}

VideoInterviewInstructions.propTypes = {
  setReadInstructions: PropTypes.func.isRequired,
}

function PreVideoInterview({
  rescheduleInterview,
  rescheduleEnabled,
  isFinished,
  interviewTime,
  setShowCounter,
  showCounter,
}) {
  const { t } = useTranslation()
  const [readInstructions, setReadInstructions] = useState(false)
  const [testVideo, setTestVideo] = useState(false)
  const deviceType = useDeviceType()
  const isMobile = deviceType !== 'desktop'

  useEffect(() => {
    if (!showCounter) {
      setTestVideo(true)
    }
  }, [showCounter])

  const renderContent = () => {
    switch (true) {
      case testVideo:
        return (
          <TestVideo
            rescheduleInterview={rescheduleInterview}
            rescheduleEnabled={rescheduleEnabled}
            isFinished={isFinished}
          />
        )
      case !readInstructions:
        return (
          <VideoInterviewInstructions
            setReadInstructions={setReadInstructions}
          />
        )
      default:
        return (
          <ContentContainer padding={isMobile ? '15px 10px' : '30px'}>
            <Timer
              interviewTime={interviewTime}
              setShowCounter={setShowCounter}
            />
            {rescheduleEnabled && !isFinished && (
              <div className="d-flex justify-content-end align-items-center p-3">
                <PrimaryButton
                  color={theme.talentinoSpecialBlue}
                  backgroundcolor={theme.talentinoWhiteBlue}
                  hovercolor={theme.talentinoDarkerWhiteBlue}
                  onClick={rescheduleInterview}
                  style={{
                    fontSize: isMobile ? '12px' : '14px',
                    whiteSpace: 'nowrap',
                    padding: isMobile ? '5px 15px' : '5px 40px',
                  }}
                >
                  {t('preInterview.rescheduleInterview')}
                </PrimaryButton>
              </div>
            )}
          </ContentContainer>
        )
    }
  }

  return (
    <MainContainer>
      <ImageContainer>
        <img
          height="auto"
          width={isMobile ? '150px' : '200px'}
          src={FullLogo}
          alt=""
          style={{ margin: isMobile ? '10px 0' : '20px 0' }}
        />
      </ImageContainer>
      {renderContent()}
    </MainContainer>
  )
}

PreVideoInterview.propTypes = {
  rescheduleInterview: PropTypes.func,
  interviewTime: PropTypes.string.isRequired,
  setShowCounter: PropTypes.func.isRequired,
  showCounter: PropTypes.bool.isRequired,
  rescheduleEnabled: PropTypes.bool,
  isFinished: PropTypes.bool,
}

PreVideoInterview.defaultProps = {
  rescheduleInterview: () => {},
  rescheduleEnabled: false,
  isFinished: false,
}

export default PreVideoInterview
